import React, { useMemo, useState } from "react";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";

const AMOUNTS = [
  { v: 500,  sub: "Kiçik ehtiyaclar üçün" },
  { v: 1000, sub: "Tez həll" },
  { v: 1500, sub: "Daha çox imkan" },
  { v: 2000, sub: "Daha geniş imkan" },
  { v: 2500, sub: "Rahat seçim" },
  { v: 3000, sub: "Sərfəli seçim" },
  { v: 3500, sub: "Böyük planlar üçün" },
  { v: 4000, sub: "Maksimum məbləğ" },
];
const PERIODS = [12, 24, 36];
const ANNUAL_RATE = 0.1;

const STEP_LABELS = [
  ["Məbləğ", "seçimi"],
  ["Müddət", "seçimi"],
  ["Məlumatlar"],
  ["Nəticə"],
];

export default function CreditApproved({ go, notify, user, onSubmit }) {
  const [step, setStep] = useState(0);
  const [amount, setAmount] = useState(4000);
  const [period, setPeriod] = useState(24);

  const summary = useMemo(() => {
    const t = amount * (1 + ANNUAL_RATE * (period / 12));
    return { total: t, monthly: t / period };
  }, [amount, period]);

  const handleContinue = () => {
    if (step === 0) return setStep(1);
    if (step === 1) return setStep(2);
    if (step === 2) return setStep(3);
    onSubmit({ amount, period, total: summary.total, monthly: summary.monthly });
  };

  return (
    <section className="screen csx-screen" data-testid="credit-approved-screen">
      <button
        className="csx-back"
        onClick={() => (step === 0 ? go("home") : setStep(step - 1))}
        data-testid="csx-back-btn"
        aria-label="Geri"
      >
        <ArrowLeft size={20} strokeWidth={2.6} />
      </button>
      <div className="csx-content">
        {/* Brand header */}
        <div className="csx-brand">
          <div className="csx-brand-logo">
            <img src="/assets/logo.png" alt="" className="csx-brand-logo-img" />
          </div>
          <div className="csx-brand-text">
            <div className="csx-brand-name">Pulpay Kredit</div>
          </div>
        </div>

        {/* Step content */}
        {step === 0 && (
          <>
            <h2 className="csx-title">Kredit məbləğini seçin</h2>
            <p className="csx-sub">
              Sizə uyğun məbləği seçin, sonrakı mərhələdə<br />
              müddəti müəyyən edəcəksiniz.
            </p>
            <div className="csx-grid" data-testid="csx-amounts">
              {AMOUNTS.map(({ v }) => (
                <button
                  key={v}
                  type="button"
                  className={`csx-card ${amount === v ? "active" : ""}`}
                  onClick={() => setAmount(v)}
                  data-testid={`csx-amount-${v}`}
                >
                  <div className="csx-card-body">
                    <div className="csx-card-title">
                      {v.toLocaleString("az-AZ").replace(",", ".")} <em>AZN</em>
                    </div>
                  </div>
                  <span className={`csx-radio ${amount === v ? "on" : ""}`}>
                    {amount === v && <span className="csx-radio-dot" />}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <h2 className="csx-title">Ödəmə müddətini seçin</h2>
            <p className="csx-sub">Aylıq ödəniş uyğun müddət seçərək davam edin.</p>
            <div className="csx-grid csx-grid-1" data-testid="csx-periods">
              {PERIODS.map((p) => {
                const t = amount * (1 + ANNUAL_RATE * (p / 12));
                const m = Math.round(t / p);
                return (
                  <button
                    key={p}
                    className={`csx-card ${period === p ? "active" : ""}`}
                    onClick={() => setPeriod(p)}
                    data-testid={`csx-period-${p}`}
                  >
                    <div className="csx-card-body">
                      <div className="csx-card-title">{p} ay</div>
                      <div className="csx-card-sub">Aylıq ödəniş: {m} AZN</div>
                    </div>
                    <span className={`csx-radio ${period === p ? "on" : ""}`}>
                      {period === p && <span className="csx-radio-dot" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {step === 2 && user && (
          <>
            <h2 className="csx-title csx-title-boxed">Məlumatlarınızı yoxlayın</h2>
            <p className="csx-sub">Bütün məlumatlar doğrudursa, davam edin.</p>

            {/* User identity card */}
            <div className="csx-review-user" data-testid="csx-review-user">
              <div className="csx-review-avatar">
                <img src="/assets/logo.png" alt="" />
              </div>
              <div className="csx-review-uinfo csx-review-uinfo-detailed">
                <div className="csx-review-row">
                  <span className="csx-review-lbl">Ad</span>
                  <span className="csx-review-val">{user.firstName}</span>
                </div>
                <div className="csx-review-row">
                  <span className="csx-review-lbl">Soyad</span>
                  <span className="csx-review-val">{user.lastName}</span>
                </div>
                <div className="csx-review-row">
                  <span className="csx-review-lbl">FIN</span>
                  <span className="csx-review-val">{user.fin}</span>
                </div>
              </div>
            </div>

            {/* Highlight tiles */}
            <div className="csx-review-hlrow">
              <div className="csx-review-hl">
                <span className="csx-review-hl-label">Kredit məbləği</span>
                <span className="csx-review-hl-value">{amount.toLocaleString("az-AZ")} <em>AZN</em></span>
              </div>
              <div className="csx-review-hl csx-review-hl-alt">
                <span className="csx-review-hl-label">Aylıq ödəniş</span>
                <span className="csx-review-hl-value">{Math.round(summary.monthly)} <em>AZN</em></span>
              </div>
            </div>

            {/* Detail rows */}
            <div className="csx-review">
              <div className="csx-review-sec">
                <span className="csx-review-sec-title">Şəxsi məlumatlar</span>
                <div className="csx-review-row"><span>Doğum tarixi</span><b>{user.birthDate}</b></div>
                <div className="csx-review-row"><span>Vəsiqə seriyası</span><b>{user.docSeries}</b></div>
                <div className="csx-review-row"><span>Vəsiqə nömrəsi</span><b>{user.docNumber}</b></div>
              </div>
              <div className="csx-review-sec">
                <span className="csx-review-sec-title">Kredit şərtləri</span>
                <div className="csx-review-row"><span>Müddət</span><b>{period} ay</b></div>
                <div className="csx-review-row"><span>Faiz dərəcəsi</span><b>10% illik</b></div>
                <div className="csx-review-row"><span>Ümumi ödəniş</span><b>{Math.round(summary.total).toLocaleString("az-AZ")} AZN</b></div>
              </div>
            </div>

            <div className="csx-review-note">
              <span className="csx-review-note-ic">i</span>
              <span>Davam etməklə şərt və qaydalarla razı olmuş sayılırsınız.</span>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h2 className="csx-title">Təsdiq olundu 🎉</h2>
            <p className="csx-sub">Krediti təsdiqləmək üçün Davam edin.</p>
            <div className="csx-info">
              <div className="csx-info-row csx-info-hi"><span>Kredit məbləği</span><b>{amount.toLocaleString("az-AZ")} AZN</b></div>
              <div className="csx-info-row"><span>Müddət</span><b>{period} ay</b></div>
              <div className="csx-info-row"><span>Aylıq ödəniş</span><b>{Math.round(summary.monthly)} AZN</b></div>
              <div className="csx-info-row"><span>Ümumi ödəniş</span><b>{Math.round(summary.total).toLocaleString("az-AZ")} AZN</b></div>
            </div>
          </>
        )}

        <button className="csx-cta" onClick={handleContinue} data-testid="csx-continue-btn">
          {step === 3 ? "Təsdiq et" : "Davam et"}
          <ArrowRight size={20} strokeWidth={2.8} />
        </button>
      </div>
    </section>
  );
}
